import { isNullOrUndefined } from '@adaptiva/core/common';
import { MenuItem } from '@adaptiva/core/menus';
import { UiService } from '@adaptiva/core/ui';
import { BaseWidgetComponent, WidgetService } from '@adaptiva/core/widgets';
import { ECacheMode, EFetchMode, EJavaClassNames, EObjectType, IObjectTableListConfiguration, ObjectManagerService } from '@adaptiva/data-service';
import { IMetadataAuthoringRule, IStandaloneMetadataAuthoringRule, MetadataAuthoringRule, MetadataObject, StandaloneMetadataAuthoringRule } from '@adaptiva/metadata/common';
import { OverlayComponent } from '@adaptiva/widgets/overlay';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewChild, forwardRef } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { AuthoringRuleNode } from './classes';
import { IAuthoringRuleNode, IReferenceMetadataAuthoringRule } from './interfaces';
import { MainAuthoringRuleMenuController } from './menus';
import { AuthoringRuleUserInput } from './types';

@Component({
	standalone: false,
	selector: 'ada-authoring-rule',
	templateUrl: './authoring-rule.component.html',
	styleUrls: ['./authoring-rule.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		MainAuthoringRuleMenuController,
		WidgetService,
		{ provide: BaseWidgetComponent, useExisting: forwardRef(() => AuthoringRuleComponent) }
	]
})
export class AuthoringRuleComponent extends BaseWidgetComponent {

	@Input()
	public availableProperties: BehaviorSubject<string[]> = new BehaviorSubject([]);

	@Input()
	public expandOnInit: boolean = true;

	@Input()
	public serverObject$: BehaviorSubject<MetadataObject>;

	@Output()
	public valueChange: EventEmitter<IMetadataAuthoringRule> = new EventEmitter();

	@ViewChild('SensorExpressionOverlay')
	public sensorExpressionOverlay: OverlayComponent;

	@ViewChild('ReferenceOverlay')
	public referenceOverlay: OverlayComponent;

	private _value: IMetadataAuthoringRule;

	public nodes: IAuthoringRuleNode[];

	private _references: IStandaloneMetadataAuthoringRule[] = [];

	/* OVERLAY PROPERTIES */

	public overlayNode: IAuthoringRuleNode;

	public overlayType: int;

	public overlayUserInput: AuthoringRuleUserInput;

	public overlayEditing: boolean = false;

	public expressionError$: BehaviorSubject<string> = new BehaviorSubject('');

	/* CLASS REFERENCES */

	public RuleType: typeof MetadataAuthoringRule = MetadataAuthoringRule;

	public authoringRuleClass: EJavaClassNames = EJavaClassNames.StandaloneMetadataAuthoringRule;

	/* OPTIONS */

	public standaloneQueryConfig: IObjectTableListConfiguration = {
		objectClassName: EJavaClassNames.StandaloneMetadataAuthoringRule,
		parentFolderId: 0,
		sortColumn: 'name',
		ascending: true
	};

	constructor(
		public UI: UiService,
		public widgetService: WidgetService,
		protected mainMenuController: MainAuthoringRuleMenuController,
		private _objectManager: ObjectManagerService
	) {
		super(widgetService);
		this._registerMenuCallbacks();
	}


	/* TREE METHODS */

	private _createTree(): void {
		const _referenceIds: long[] = [...new Set(this._getReferenceIds(this._value))];
		if (_referenceIds?.length) {
			this._fetchReferences(_referenceIds).subscribe({
				next: _references => {
					this._references = _references;
					this.nodes = [this._generateNode(this._value)];
					this._cd.detectChanges();
				},
				error: _err => {
					this._widgetService.logging.error(
						'Failed to fetch Authoring Rule References',
						this._widgetService.logging.extractErrorMessage(_err)
					)();
				}
			});
		} else {
			this.nodes = [this._generateNode(this._value)];
			this._cd.detectChanges();
		}
	}

	private _generateNode(_rule: IMetadataAuthoringRule, _parent?: IAuthoringRuleNode): IAuthoringRuleNode {
		const _ruleNode: IAuthoringRuleNode = new AuthoringRuleNode(_rule.type, _parent);
		_ruleNode.isExpanded = this.expandOnInit;
		let _ruleChildren: IMetadataAuthoringRule[];

		if (_rule['children']?.length)
			_ruleChildren = _rule['children'];
		else if (_rule['child'])
			_ruleChildren = [_rule['child']];

		if (_ruleChildren?.length) {
			const _children: IAuthoringRuleNode[] = _ruleChildren.map(_child => this._generateNode(_child, _ruleNode));
			if (_children?.length)
				_ruleNode.children = _children;
		} else {
			_ruleNode.setUserInput(
				_rule['expression'] || _rule['reference'] || '',
				this._getReferenceName(_rule.type, _rule['reference'])
			);
		}
		return _ruleNode;
	}

	private _fetchReferences(_referenceIds: long[]): Observable<IStandaloneMetadataAuthoringRule[]> {
		return this._objectManager.getObjectsByIds<StandaloneMetadataAuthoringRule>(
			EJavaClassNames.StandaloneMetadataAuthoringRule,
			_referenceIds,
			EObjectType.VISIBLE,
			EFetchMode.COMPLETE,
			ECacheMode.CACHE
		).pipe(
			take(1)
		);
	}

	private _getReferenceIds(_rule: MetadataAuthoringRule): long[] {
		let _ids: long[] = [];
		let _children = [];

		if (_rule.type === MetadataAuthoringRule.AND || _rule.type === MetadataAuthoringRule.OR)
			_children = _rule['children'];
		else if (_rule.type === MetadataAuthoringRule.NOT && _rule['child'])
			_children = [_rule['child']];

		if (_children?.length) {
			const _childIds: long[][] = _children.map(_child => this._getReferenceIds(_child));
			_ids = _ids.concat([].concat(..._childIds));
		}

		if (_rule.type === MetadataAuthoringRule.REFERENCE)
			_ids.push((_rule as IReferenceMetadataAuthoringRule).reference);

		return _ids;
	}

	public setValue(_rule: MetadataAuthoringRule): void {
		if (_rule) {
			this._value = _rule;
			this._createTree();
		} else {
			this._value = undefined;
			this.nodes = [];
		}
		this._cd.detectChanges();
	}

	private _output(): void {
		const _head: IAuthoringRuleNode = this.nodes[0];
		if (_head) {
			const _rule: MetadataAuthoringRule = _head.createClass() as MetadataAuthoringRule;
			this.valueChange.emit(_rule);
		} else
			this.valueChange.emit(null);
	}

	/* NODE METHODS */

	public addRuleOperator(type: int, _node?: IAuthoringRuleNode): void {
		if (!isNullOrUndefined(_node))
			_node.addChild(type);
		else
			this.nodes = [new AuthoringRuleNode(type)];
		this._output();
	}

	public removeNode(_node: IAuthoringRuleNode): void {
		if (_node.parentNode)
			_node.parentNode.removeChild(_node.id);
		else {
			const _nodeIndex: number = this.nodes.findIndex(_n => _n.id === _node.id);
			if (_nodeIndex > -1)
				this.nodes.splice(_nodeIndex, 1);
		}
		this._output();
	}

	/* OVERLAY METHODS */

	public openSensorExpressionOverlay(_node?: IAuthoringRuleNode): void {
		this.expressionError$.next('');
		this.overlayNode = _node || undefined;
		if (this.overlayEditing) {
			this.overlayType = _node.type;
			this.overlayUserInput = _node.userInput;
		} else {
			this.overlayType = MetadataAuthoringRule.EXPRESSION;
			this.overlayUserInput = '';
		}
		this.sensorExpressionOverlay?.open();
	}

	public openReferenceOverlay(_node?: IAuthoringRuleNode): void {
		this.overlayNode = _node || undefined;
		if (this.overlayEditing) {
			this.overlayType = _node.type;
			this.overlayUserInput = _node.userInput;
		} else {
			this.overlayType = MetadataAuthoringRule.REFERENCE;
			this.overlayUserInput = undefined;
		}
		this.referenceOverlay?.open();
	}

	public updateOverlayValue(_value: any, _index?: number): void {
		if (_index === undefined)
			this.overlayUserInput = _value;
		else
			this.overlayUserInput[_index].value = _value;
	}

	public submitRuleOverlay(): void {
		const _referenceName: string = this._getReferenceName(this.overlayType, this.overlayUserInput as long);
		if (this.overlayEditing)
			this.overlayNode.setUserInput(this.overlayUserInput, _referenceName);
		else {
			if (this.overlayNode)
				this.overlayNode.addChild(this.overlayType, this.overlayUserInput, _referenceName);
			else {
				// Handle the case where an Overlay Node has not been provided (Default Menu)
				// Add a node at the root of the tree w/ the Overlay data
				this.nodes = [new AuthoringRuleNode(this.overlayType, null, this.overlayUserInput, _referenceName)];
			}
		}
		this.overlayEditing = false;
		this._output();
	}

	public cancelOverlay(_state: boolean): void {
		this.overlayEditing = false;
	}

	public editRule(_node: IAuthoringRuleNode, _type: int): void {
		this.overlayEditing = true;
		if (_type === MetadataAuthoringRule.EXPRESSION)
			this.openSensorExpressionOverlay(_node);
		else if (_type === MetadataAuthoringRule.REFERENCE)
			this.openReferenceOverlay(_node);
	}

	public selectReference(_rule: IStandaloneMetadataAuthoringRule): void {
		if (!this._references.map(_ref => _ref.objectId).includes(_rule.objectId))
			this._references.push(_rule);

		this.overlayUserInput = _rule ? _rule.objectId : null;
	}

	public validateExpression(): boolean {
		const _isValid: boolean = !!this.overlayUserInput;
		this.expressionError$.next(_isValid ? '' : 'You must supply a Sensor Expression');
		return _isValid;
	}

	private _getReferenceName(_type: int, _reference: long): string {
		let _name: string = '';
		if (_type === MetadataAuthoringRule.REFERENCE)
			_name = this._references.find(_ref => _ref.objectId === _reference)?.name;
		return _name;
	}

	private _registerMenuCallbacks(): void {
		this.mainMenuController.on(
			this.mainMenuController.andMenuName,
			(_menu: MenuItem<IAuthoringRuleNode>) => this.addRuleOperator(MetadataAuthoringRule.AND, _menu.data)
		);
		this.mainMenuController.on(
			this.mainMenuController.notMenuName,
			(_menu: MenuItem<IAuthoringRuleNode>) => this.addRuleOperator(MetadataAuthoringRule.NOT, _menu.data)
		);
		this.mainMenuController.on(
			this.mainMenuController.orMenuName,
			(_menu: MenuItem<IAuthoringRuleNode>) => this.addRuleOperator(MetadataAuthoringRule.OR, _menu.data)
		);
		this.mainMenuController.on(
			this.mainMenuController.selectReferenceMenuName,
			(_menu: MenuItem<IAuthoringRuleNode>) => this.openReferenceOverlay(_menu.data)
		);
		this.mainMenuController.on(
			this.mainMenuController.createExpressionMenuName,
			(_menu: MenuItem<IAuthoringRuleNode>) => this.openSensorExpressionOverlay(_menu.data)
		);
		this.mainMenuController.on(
			this.mainMenuController.removeMenuName,
			(_menu: MenuItem<IAuthoringRuleNode>) => this.removeNode(_menu.data)
		);
		this.mainMenuController.on(
			this.mainMenuController.editExpressionMenuName,
			(_menu: MenuItem<IAuthoringRuleNode>) => this.editRule(_menu.data, MetadataAuthoringRule.EXPRESSION)
		);
		this.mainMenuController.on(
			this.mainMenuController.editReferenceMenuName,
			(_menu: MenuItem<IAuthoringRuleNode>) => this.editRule(_menu.data, MetadataAuthoringRule.REFERENCE)
		);
	}

}
